<template>
  <div class="card">
    <div class="flex justify-between items-center mb-2">
      <h2 class="text-base font-semibold">{{ isEditing ? 'Edit Summary Template' : 'Create Summary Template' }}</h2>
      <div class="flex gap-1.5">
        <Button label="Cancel" icon="pi pi-times" text size="small" @click="$emit('cancel')" />
        <Button label="Save" icon="pi pi-check" size="small" @click="save" :loading="saving" />
      </div>
    </div>

    <!-- Template metadata -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
      <div class="flex flex-col gap-1.5">
        <label class="font-semibold text-sm">Template Name</label>
        <InputText v-model="form.name" placeholder="e.g. CGPA Summary Sheet" />
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="font-semibold text-sm">Description</label>
        <InputText v-model="form.description" placeholder="Description..." />
      </div>
    </div>

    <!-- Properties -->
    <div class="bg-primary/10 border border-primary/20 rounded-lg p-2.5 mb-3 text-sm">
      <h4 class="font-semibold mb-2 text-primary-700 text-sm">Template Properties</h4>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Calculation Method</label>
          <Select v-model="form.calculationMethod" :options="methodOptions" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <div class="flex items-center gap-2 mt-3">
          <Checkbox v-model="form.showGradeSummary" :binary="true" inputId="showGrade" />
          <label for="showGrade" class="text-xs">Show Grade Summary</label>
        </div>
        <div class="flex items-center gap-2 mt-3">
          <Checkbox v-model="form.showFailedCourses" :binary="true" inputId="showFailed" />
          <label for="showFailed" class="text-xs">Show Failed Courses</label>
        </div>
        <div class="flex items-center gap-2 mt-3">
          <Checkbox v-model="form.showLegend" :binary="true" inputId="showLegend" />
          <label for="showLegend" class="text-xs">Show Legend</label>
        </div>
      </div>
    </div>

    <!-- Classification & Graduation Settings -->
    <div class="bg-primary/10 border border-primary/20 rounded-lg mb-3 text-sm overflow-hidden">
      <button @click="showClassification = !showClassification"
        class="w-full flex justify-between items-center p-2.5 hover:bg-primary/10 transition-colors cursor-pointer">
        <span class="font-semibold text-primary-700 text-sm flex items-center">
          <i class="pi pi-sliders-h mr-2 text-xs"></i> Classification & Graduation Settings
        </span>
        <i :class="showClassification ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs text-primary-700" />
      </button>

      <div v-if="showClassification" class="px-3 pb-3 border-t border-primary/20 pt-3 space-y-4">
        <!-- Graduating Categories -->
        <div>
          <h4 class="text-xs font-semibold text-gray-600 mb-1">Graduating Categories</h4>
          <p class="text-[10px] text-gray-400 mb-1.5">Classification labels for final-year students. Ordered from highest to lowest threshold.</p>
          <div class="space-y-1">
            <div v-for="(cat, idx) in form.graduatingCategories" :key="'grad-'+idx" class="grid grid-cols-[1fr_6rem_1fr_auto] items-center gap-2">
              <InputText :modelValue="cat.label"
                @update:modelValue="v => { cat.label = v ?? ''; cat.key = cat.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || cat.key; }"
                size="small" placeholder="Label (e.g. Distinction)" />
              <InputNumber v-model="cat.min_threshold" size="small" placeholder="Min"
                :min-fraction-digits="0" :max-fraction-digits="2" :style="{ width: '100%' }" />
              <InputText v-model="cat.status" size="small" placeholder="Status note (optional)" />
              <Button icon="pi pi-times" severity="danger" text rounded size="small"
                @click="form.graduatingCategories.splice(idx, 1)" />
            </div>
          </div>
          <Button label="Add Category" icon="pi pi-plus" text size="small" class="mt-1"
            @click="form.graduatingCategories.push({ label: '', key: 'custom_' + Date.now(), min_threshold: 0, status: null })" />
        </div>

        <!-- Non-Graduating Statuses -->
        <div>
          <h4 class="text-xs font-semibold text-gray-600 mb-1">Non-Graduating Statuses</h4>
          <p class="text-[10px] text-gray-400 mb-1.5">Status labels for students who haven't completed the programme. Use a single entry with no threshold for a catch-all like "Incomplete Result".</p>
          <div class="space-y-1">
            <div v-for="(cat, idx) in form.nonGraduatingStatuses" :key="'nongrad-'+idx" class="grid grid-cols-[1fr_6rem_auto] items-center gap-2">
              <InputText :modelValue="cat.label"
                @update:modelValue="v => { cat.label = v ?? ''; cat.key = cat.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || cat.key; }"
                size="small" placeholder="Label (e.g. Incomplete Result)" />
              <InputNumber v-model="cat.min_threshold" size="small" placeholder="Min"
                :min-fraction-digits="0" :max-fraction-digits="2" :style="{ width: '100%' }" />
              <Button icon="pi pi-times" severity="danger" text rounded size="small"
                @click="form.nonGraduatingStatuses.splice(idx, 1)" />
            </div>
          </div>
          <Button label="Add Status" icon="pi pi-plus" text size="small" class="mt-1"
            @click="form.nonGraduatingStatuses.push({ label: '', key: 'custom_' + Date.now(), min_threshold: null })" />
        </div>

        <!-- Status Labels & Degree Class Visibility -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Graduating Label</label>
            <InputText v-model="form.graduatingStatusLabels.passing" size="small" placeholder="Graduating" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Not Graduating Prefix</label>
            <InputText v-model="form.graduatingStatusLabels.failingPrefix" size="small" placeholder="Not Graduating" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Degree Class Column</label>
            <Select v-model="form.degreeClassVisibility" :options="degreeClassOptions"
              optionLabel="label" optionValue="value" size="small" class="w-full" />
          </div>
        </div>

        <!-- Graduation Conditions -->
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1.5">
          <p class="text-xs text-amber-800 font-medium">Graduation Conditions</p>
          <p class="text-[10px] text-amber-700">These control when students are classified using Graduating Categories vs Non-Graduating Statuses.</p>
          <label class="flex items-center gap-2 cursor-pointer text-xs">
            <Checkbox v-model="form.checkFinalYear" :binary="true" />
            Must be in final year / extra year
          </label>
          <p v-if="!form.checkFinalYear" class="text-[10px] text-amber-600 ml-6">All students will be classified using Graduating Categories regardless of their year.</p>
          <label class="flex items-center gap-2 cursor-pointer text-xs">
            <Checkbox v-model="form.checkOutstandingCourses" :binary="true" />
            Must have no outstanding compulsory courses
          </label>
          <label class="flex items-center gap-2 cursor-pointer text-xs">
            <Checkbox v-model="form.checkMinUnits" :binary="true" />
            Must meet minimum credit units requirement
          </label>
        </div>
      </div>
    </div>

    <!-- PDF & Layout Settings -->
    <div class="bg-primary/10 border border-primary/20 rounded-lg mb-3 text-sm overflow-hidden">
      <button @click="showPdfSettings = !showPdfSettings"
        class="w-full flex justify-between items-center p-2.5 hover:bg-primary/10 transition-colors cursor-pointer">
        <span class="font-semibold text-primary-700 text-sm flex items-center">
          <i class="pi pi-file-pdf mr-2 text-xs"></i> PDF & Layout Settings
        </span>
        <i :class="showPdfSettings ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs text-primary-700" />
      </button>

      <div v-if="showPdfSettings" class="px-3 pb-3 border-t border-primary/20 pt-3 space-y-4">
        <!-- Header Overrides -->
        <div>
          <h4 class="text-xs font-semibold text-gray-600 mb-1">Header Overrides</h4>
          <p class="text-[10px] text-gray-400 mb-1.5">Leave blank to use the default from your school profile.</p>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Report Title</label>
              <InputText v-model="form.reportTitle" size="small"
                placeholder="SUMMARY FOR PRESENTATION OF EXAMINATION RESULTS" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">School Name Override</label>
              <InputText v-model="form.headerSchoolName" size="small"
                placeholder="(uses school profile name)" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Affiliate Text Override</label>
              <InputText v-model="form.headerAffiliateText" size="small"
                placeholder="(uses school profile affiliate)" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Logo Position</label>
              <Select v-model="form.headerLogoPosition" :options="logoPositionOptions"
                optionLabel="label" optionValue="value" size="small" class="w-full" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Semester Label</label>
              <InputText v-model="form.headerSemesterLabel" size="small" placeholder="SEMESTER:" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Semester Format</label>
              <Select v-model="form.headerSemesterFormat" :options="semesterFormatOptions"
                optionLabel="label" optionValue="value" size="small" class="w-full" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Level Label</label>
              <InputText v-model="form.headerLevelLabel" size="small" placeholder="LEVEL:" />
            </div>
          </div>
          <!-- Level Display Map -->
          <div class="mt-2">
            <label class="text-[10px] text-gray-500 block mb-1">Level Display Map (e.g. show "DIPLOMA ONE" instead of "100")</label>
            <div class="space-y-1">
              <div v-for="(entry, idx) in form.headerLevelDisplayMap" :key="'lvl-'+idx"
                class="grid grid-cols-[6rem_1fr_auto] items-center gap-2">
                <InputText v-model="entry.level" size="small" placeholder="100" />
                <InputText v-model="entry.display" size="small" placeholder="DIPLOMA ONE" />
                <Button icon="pi pi-times" severity="danger" text rounded size="small"
                  @click="form.headerLevelDisplayMap.splice(idx, 1)" />
              </div>
            </div>
            <Button label="Add Level Mapping" icon="pi pi-plus" text size="small" class="mt-1"
              @click="form.headerLevelDisplayMap.push({ level: '', display: '' })" />
          </div>
        </div>

        <!-- Paper & Layout -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Paper Size</label>
            <Select v-model="form.paperSize" :options="paperSizeOptions" optionLabel="label" optionValue="value" size="small" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Orientation</label>
            <Select v-model="form.orientation" :options="orientationOptions" optionLabel="label" optionValue="value" size="small" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Rows Per Page</label>
            <InputNumber v-model="form.rowsPerPage" size="small" :min="5" :max="50" :style="{ width: '100%' }" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Max Columns Per Page</label>
            <InputNumber v-model="form.maxColumnsPerPage" size="small" :min="5" :max="40" :style="{ width: '100%' }" />
          </div>
        </div>

        <!-- Section Heading Labels -->
        <div>
          <h4 class="text-xs font-semibold text-gray-600 mb-1">Section Heading Labels</h4>
          <p class="text-[10px] text-gray-400 mb-1.5">Rename the headings displayed above each section of the PDF. This only changes the title text, not the content underneath.</p>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Class Summary</label>
              <InputText v-model="form.sectionTitles.classSummary" size="small" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Interpretation</label>
              <InputText v-model="form.sectionTitles.interpretation" size="small" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Failed Courses</label>
              <InputText v-model="form.sectionTitles.failedCourses" size="small" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-gray-500">Course Key</label>
              <InputText v-model="form.sectionTitles.courseKey" size="small" />
            </div>
          </div>
        </div>

        <!-- Custom Legend Sections -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <h4 class="text-xs font-semibold text-gray-600">Custom Legend Sections</h4>
            <div class="flex items-center gap-1.5">
              <label class="text-[10px] text-gray-500">Columns:</label>
              <Select v-model="form.legendColumns" :options="[{label:'1',value:1},{label:'2',value:2},{label:'3',value:3}]"
                optionLabel="label" optionValue="value" class="w-16" size="small" />
            </div>
          </div>
          <p class="text-[10px] text-gray-400 mb-1.5">When set, these replace the auto-generated interpretation. Each section can have an optional heading. Leave empty to auto-generate from categories.</p>
          <div class="space-y-3">
            <div v-for="(group, gIdx) in form.legendGroups" :key="'lgroup-'+gIdx"
              class="border border-gray-200 rounded-md p-2 bg-white">
              <div class="flex items-center gap-2 mb-1.5">
                <InputText v-model="group.heading" size="small" class="flex-1"
                  placeholder="Section heading (optional, e.g. Classification)" />
                <Button icon="pi pi-times" severity="danger" text rounded size="small"
                  @click="form.legendGroups.splice(gIdx, 1)" v-tooltip.top="'Remove section'" />
              </div>
              <div class="space-y-1 ml-2">
                <div v-for="(line, lIdx) in group.lines" :key="'lline-'+gIdx+'-'+lIdx"
                  class="grid grid-cols-[1fr_auto] items-center gap-1">
                  <InputText v-model="group.lines[lIdx]" size="small"
                    placeholder="e.g. DISTINCTION = 4.5 - 5.0" />
                  <Button icon="pi pi-times" severity="danger" text rounded size="small"
                    @click="group.lines.splice(lIdx, 1)" />
                </div>
              </div>
              <Button label="Add Line" icon="pi pi-plus" text size="small" class="mt-1 ml-2"
                @click="group.lines.push('')" />
            </div>
          </div>
          <Button label="Add Section" icon="pi pi-plus" text size="small" class="mt-1.5"
            @click="form.legendGroups.push({ heading: '', lines: [''] })" />
        </div>

        <!-- Signatures -->
        <div>
          <div class="flex items-center gap-2 mb-1">
            <Checkbox v-model="form.showSignatures" :binary="true" inputId="showSigs" />
            <label for="showSigs" class="text-xs font-semibold text-gray-600">Show Signatures</label>
          </div>
          <div v-if="form.showSignatures" class="space-y-1">
            <div v-for="(sig, idx) in form.signatures" :key="'sig-'+idx" class="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
              <InputText v-model="sig.title" size="small" placeholder="Role (e.g. Dean)" />
              <label class="flex items-center gap-1 text-[10px] text-gray-500">
                <Checkbox v-model="sig.show_sign" :binary="true" /> Sign
              </label>
              <label class="flex items-center gap-1 text-[10px] text-gray-500">
                <Checkbox v-model="sig.show_date" :binary="true" /> Date
              </label>
              <Button icon="pi pi-times" severity="danger" text rounded size="small"
                @click="form.signatures.splice(idx, 1)" />
            </div>
            <Button label="Add Signatory" icon="pi pi-plus" text size="small" class="mt-1"
              @click="form.signatures.push({ title: '', show_sign: true, show_date: true })" />
          </div>
        </div>
      </div>
    </div>

    <!-- Help / Hints Section -->
    <div class="bg-primary/10 border border-primary/20 rounded-lg mb-3 text-xs text-primary-700 overflow-hidden">
      <button @click="showHelp = !showHelp" class="w-full flex justify-between items-center p-2.5 hover:bg-primary/10 transition-colors cursor-pointer">
        <span class="font-semibold flex items-center"><i class="pi pi-info-circle mr-2"></i> How to build a Summary Template</span>
        <i :class="showHelp ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs" />
      </button>
      <div v-if="showHelp" class="px-3 pb-3 border-t border-primary/20 pt-2 space-y-3">
        <p>
          A summary template defines the academic performance sheet for tertiary institutions.
          It calculates GPA, CGPA, academic standing, and degree classification for each student based on their course registrations and scores.
        </p>

        <div>
          <h5 class="font-semibold mb-1">Template Properties</h5>
          <div class="space-y-1 ml-1">
            <div>
              <span class="font-medium text-primary-800">Calculation Method</span> &mdash; Determines how grades are computed:
              <ul class="list-disc list-inside ml-3 text-primary-700 mt-0.5">
                <li><strong>CGPA</strong> &mdash; Uses grade points (5-point or 4-point scale). GPA = Total Credit Points / Total Units Registered. This is the standard Nigerian university system.</li>
                <li><strong>Weighted</strong> &mdash; Uses raw scores multiplied by credit units. The thresholds change accordingly (e.g. First Class = 70+ instead of 4.50+).</li>
              </ul>
            </div>
            <div class="mt-1">
              <span class="font-medium text-primary-800">Show Grade Summary</span> &mdash; Appends a grade distribution table (how many A's, B's, etc.) below the results.
            </div>
            <div><span class="font-medium text-primary-800">Show Failed Courses</span> &mdash; Lists each student's failed courses in the output.</div>
            <div><span class="font-medium text-primary-800">Show Legend</span> &mdash; Appends a legend explaining abbreviations (TCP, TNUP, etc.).</div>
          </div>
        </div>

        <div>
          <h5 class="font-semibold mb-1">Column Types</h5>
          <div class="space-y-1.5 ml-1">
            <div>
              <span class="font-medium text-primary-800">STUDENT_INFO</span> &mdash; Student profile fields:
              <ul class="list-disc list-inside ml-3 text-primary-700 mt-0.5">
                <li><code class="bg-primary/10 px-1 rounded">sn</code> &mdash; Serial number (auto-numbered)</li>
                <li><code class="bg-primary/10 px-1 rounded">matric_no</code> &mdash; Matriculation number</li>
                <li><code class="bg-primary/10 px-1 rounded">names</code> &mdash; Full name</li>
              </ul>
            </div>
            <div>
              <span class="font-medium text-primary-800">CALCULATED</span> &mdash; Academic computation columns. Each one needs a <strong>Function</strong> and a <strong>Scope</strong>:
              <div class="mt-1 ml-3">
                <p class="font-medium text-primary-700 text-[10px] mb-0.5">Functions:</p>
                <ul class="list-disc list-inside text-primary-700">
                  <li><strong>TCP</strong> (Total Credit Points) &mdash; Sum of (grade point &times; credit units) for passed courses</li>
                  <li><strong>TNUP</strong> (Total Number of Units Passed) &mdash; Sum of credit units for courses the student passed</li>
                  <li><strong>TNUR</strong> (Total Number of Units Registered) &mdash; Sum of credit units for all registered courses</li>
                  <li><strong>GPA</strong> (Grade Point Average) &mdash; TCP &divide; TNUR for a single semester</li>
                  <li><strong>CGPA</strong> (Cumulative Grade Point Average) &mdash; Cumulative TCP &divide; Cumulative TNUR across all semesters</li>
                </ul>
                <p class="font-medium text-primary-700 text-[10px] mt-1.5 mb-0.5">Scopes:</p>
                <ul class="list-disc list-inside text-primary-700">
                  <li><strong>Previous</strong> &mdash; All semesters <em>before</em> the current one</li>
                  <li><strong>Current</strong> &mdash; This semester only</li>
                  <li><strong>Cumulative</strong> &mdash; All semesters including the current one</li>
                </ul>
              </div>
            </div>
            <div>
              <span class="font-medium text-primary-800">STATUS</span> &mdash; Outcome / classification columns:
              <ul class="list-disc list-inside ml-3 text-primary-700 mt-0.5">
                <li><strong>STATUS</strong> &mdash; Academic standing based on the template's classification settings</li>
                <li><strong>DEGREE_CLASS</strong> &mdash; Degree classification based on graduating categories</li>
                <li><strong>FAILED_COURSES</strong> &mdash; Lists course codes the student failed this semester</li>
              </ul>
            </div>
            <div>
              <span class="font-medium text-primary-800">CUSTOM</span> &mdash; A free-text column for any additional information.
            </div>
          </div>
        </div>

        <div>
          <h5 class="font-semibold mb-1">Column Groups</h5>
          <p>
            Use the <strong>Group</strong> field to visually group columns under a shared header in the output.
            For example, set the group to "Previous" for all previous-semester columns &mdash; they will appear under a single
            "Previous" header spanning those columns.
          </p>
        </div>

        <div class="bg-primary/10 rounded p-2">
          <h5 class="font-semibold mb-1">Quick Start &mdash; Standard CGPA Template</h5>
          <p class="mb-1">The default template includes a typical layout used by Nigerian universities:</p>
          <ol class="list-decimal list-inside space-y-0.5 ml-1">
            <li>Student info (SN, Matric No, Names)</li>
            <li>Previous semester: TCP, TNUP, TNUR, CGPA (grouped under "Previous")</li>
            <li>Current semester: TCP, TNUP, TNUR, GPA (grouped under "Current")</li>
            <li>Cumulative: TCP, TNUP, TNUR, CGPA (grouped under "Cumulative")</li>
            <li>Failed Courses, Degree Class, Status</li>
          </ol>
          <p class="mt-1.5 text-[10px] text-primary">You can reorder, add, or remove columns to match your institution's requirements. The default columns are pre-filled when you create a new template.</p>
        </div>

        <div class="bg-yellow-50 border border-yellow-200 rounded p-2 text-yellow-800">
          <h5 class="font-semibold mb-1 flex items-center text-xs"><i class="pi pi-exclamation-triangle mr-1.5 text-xs"></i> Important Notes</h5>
          <ul class="list-disc list-inside space-y-0.5 ml-1">
            <li>Each column must have a <strong>unique key</strong>. Use prefixes like <code class="bg-yellow-100 px-1 rounded">prev_</code>, <code class="bg-yellow-100 px-1 rounded">curr_</code>, <code class="bg-yellow-100 px-1 rounded">cum_</code> to differentiate the same function across scopes.</li>
            <li>The <strong>Calculation Method</strong> affects degree class thresholds. CGPA method uses a 5.0 scale (First Class = 4.50+). Weighted method uses percentage scale (First Class = 70+).</li>
            <li>STATUS and DEGREE_CLASS use the CGPA/weighted score automatically &mdash; you don't need to configure a source key for them.</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Columns -->
    <div class="mb-1.5 flex justify-between items-center">
      <h3 class="font-semibold text-sm">Columns (Drag to Reorder)</h3>
      <Button label="Add Column" icon="pi pi-plus" size="small" outlined @click="toggleAddColumnMenu" />
      <Menu ref="addColumnMenuRef" :model="addColumnMenuItems" :popup="true" />
    </div>

    <!-- Header row -->
    <div class="grid grid-cols-12 gap-2 px-3 py-1.5 bg-gray-100 font-semibold text-xs text-gray-600 rounded-t-md border-b border-gray-200">
      <div class="col-span-1 text-center">#</div>
      <div class="col-span-2">Label</div>
      <div class="col-span-2">Key</div>
      <div class="col-span-2">Type</div>
      <div class="col-span-2">Group</div>
      <div class="col-span-1 text-center">Vis.</div>
      <div class="col-span-2">Function / Scope</div>
    </div>

    <div class="border border-gray-200 rounded-b-md bg-white min-h-[80px]" @dragover.prevent @drop="onDrop">
      <div v-if="form.columns.length === 0" class="text-center py-4 text-gray-500 italic text-sm">
        No columns added. Click "Add Column" to start.
      </div>

      <div v-for="(item, index) in form.columns" :key="item.tempId" draggable="true"
        @dragstart="onDragStart(index, $event)" @dragenter="onDragEnter(index)" @dragend="onDragEnd"
        class="flex flex-col w-full border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-move"
        :class="{
          'opacity-50 border-2 border-primary': draggedIndex === index,
          'border-t-4 border-primary': dropTargetIndex === index && draggedIndex !== index,
        }">

        <!-- Main row -->
        <div class="grid grid-cols-12 gap-2 items-center p-1.5">
          <div class="col-span-1 flex items-center justify-center text-gray-400">
            <i class="pi pi-bars mr-1 text-gray-300 text-xs"></i>{{ index + 1 }}
          </div>
          <div class="col-span-2">
            <InputText v-model="item.label" class="w-full text-xs p-1" placeholder="Label" />
          </div>
          <div class="col-span-2">
            <InputText v-model="item.key" class="w-full text-xs p-1 font-mono" placeholder="key" />
          </div>
          <div class="col-span-2">
            <Select v-model="item.type" :options="columnTypes" class="w-full text-xs" placeholder="Type" @change="onTypeChange(item)" />
          </div>
          <div class="col-span-2">
            <Select v-model="item.group" :options="groupOptions" optionLabel="label" optionValue="value"
              class="w-full text-xs" placeholder="None" showClear />
          </div>
          <div class="col-span-1 flex justify-center">
            <Checkbox v-model="item.is_visible" :binary="true" />
          </div>
          <div class="col-span-2 flex gap-1">
            <Select v-if="item.type === 'CALCULATED' || item.type === 'STATUS'" v-model="item.calculation_function"
              :options="item.type === 'STATUS' ? statusFunctions : calculationFunctions" class="w-full text-xs" placeholder="Function" showClear />
            <Button icon="pi pi-trash" text rounded severity="danger" size="small" @click="removeColumn(index)" />
          </div>
        </div>

        <!-- Scope picker for CALCULATED columns -->
        <div v-if="item.type === 'CALCULATED' && item.calculation_function"
          class="px-8 pb-1.5 pt-0 grid grid-cols-3 gap-2 bg-primary/10">
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium text-gray-500 w-12">Scope:</span>
            <Select v-model="item.scope" :options="scopeOptions" optionLabel="label" optionValue="value"
              class="w-full text-xs" @change="updateCalcArgs(item)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useDragDropList } from '@/composables/common/useDragDropList';
import {
  useCreateSummaryTemplateMutation,
  useUpdateSummaryTemplateMutation,
} from '@/types/vue-apollo-helpers';
import type { SummaryTemplate, SummaryTemplateColumn } from '@/types/composition-functions';

// @allow-handwritten form draft for template properties JSON blob; not a GraphQL type
interface CategoryEntry {
  label: string;
  key: string;
  min_threshold: number | null;
  status?: string | null;
}

// @allow-handwritten form draft for signature config; not a GraphQL type
interface SignatureEntry {
  title: string;
  show_sign: boolean;
  show_date: boolean;
}

// @allow-handwritten form draft for legend section groups; not a GraphQL type
interface LegendGroup {
  heading: string;
  lines: string[];
}

// @allow-handwritten editor draft for one column; mapped to CreateSummaryTemplateColumnInput on save
interface ColumnDraft {
  tempId: number;
  id: string | null;
  label: string;
  key: string;
  type: string;
  group: string | null;
  is_visible: boolean;
  calculation_function: string | null;
  scope: string | null;
  calculation_arguments?: { scope: string } | null;
}

const props = defineProps<{
  initialData?: SummaryTemplate;
  isClone?: boolean;
}>();

const emit = defineEmits(['cancel', 'saved']);
const toast = useToast();

const isEditing = computed(() => !!props.initialData && !props.isClone);
const showHelp = ref(false);
const showClassification = ref(false);
const showPdfSettings = ref(false);

const columnTypes = ['STUDENT_INFO', 'CALCULATED', 'STATUS', 'CUSTOM'];
const calculationFunctions = ['TCP', 'TNUP', 'TNUR', 'GPA', 'CGPA'];
const statusFunctions = ['STATUS', 'DEGREE_CLASS', 'FAILED_COURSES'];
const scopeOptions = [
  { label: 'Previous', value: 'previous' },
  { label: 'Current', value: 'current' },
  { label: 'Cumulative', value: 'cumulative' },
];
const groupOptions = [
  { label: 'None', value: null },
  { label: 'Previous', value: 'Previous' },
  { label: 'Current', value: 'Current' },
  { label: 'Cumulative', value: 'Cumulative' },
];
const methodOptions = [
  { label: 'CGPA (Grade Points)', value: 'cgpa' },
  { label: 'Weighted (Score × Units)', value: 'weighted' },
];
const degreeClassOptions = [
  { label: 'Auto (final year only)', value: 'auto' },
  { label: 'Always show', value: 'always' },
  { label: 'Never show', value: 'never' },
];
const paperSizeOptions = [
  { label: 'A4', value: 'A4' },
  { label: 'A3', value: 'A3' },
];
const orientationOptions = [
  { label: 'Landscape', value: 'landscape' },
  { label: 'Portrait', value: 'portrait' },
];
const logoPositionOptions = [
  { label: 'Left (default)', value: 'left' },
  { label: 'Right', value: 'right' },
];
const semesterFormatOptions = [
  { label: 'Full name (default)', value: '' },
  { label: 'Number only', value: 'number' },
];

const defaultGraduatingCategories = (method: string): CategoryEntry[] => {
  if (method === 'weighted') {
    return [
      { label: 'Distinction', key: 'distinction', min_threshold: 70, status: null },
      { label: 'Credit', key: 'credit', min_threshold: 60, status: null },
      { label: 'Pass', key: 'pass', min_threshold: 50, status: null },
      { label: 'Fail', key: 'fail', min_threshold: null, status: null },
    ];
  }
  return [
    { label: 'First Class', key: 'first_class', min_threshold: 4.50, status: null },
    { label: 'Second Class Upper', key: 'second_class_upper', min_threshold: 3.50, status: null },
    { label: 'Second Class Lower', key: 'second_class_lower', min_threshold: 2.40, status: null },
    { label: 'Third Class', key: 'third_class', min_threshold: 1.50, status: null },
    { label: 'Pass', key: 'pass', min_threshold: 1.00, status: null },
    { label: 'Fail', key: 'fail', min_threshold: null, status: null },
  ];
};

const defaultNonGraduatingStatuses = (method: string): CategoryEntry[] => {
  if (method === 'weighted') {
    return [{ label: 'Incomplete Result', key: 'incomplete', min_threshold: null }];
  }
  return [
    { label: 'GSD (Excellent)', key: 'excellent', min_threshold: 4.50 },
    { label: 'GSD (V. Good)', key: 'very_good', min_threshold: 3.50 },
    { label: 'GSD (Good)', key: 'good', min_threshold: 2.40 },
    { label: 'GSD (Average)', key: 'average', min_threshold: 1.50 },
    { label: 'PRB (Fair)', key: 'fair', min_threshold: 1.00 },
    { label: 'WRN (V. Poor)', key: 'fail', min_threshold: null },
  ];
};

const form = ref({
  id: null as string | null,
  name: '',
  description: '',
  calculationMethod: 'cgpa',
  showGradeSummary: true,
  showFailedCourses: true,
  showLegend: true,
  graduatingCategories: [] as CategoryEntry[],
  nonGraduatingStatuses: [] as CategoryEntry[],
  graduatingStatusLabels: { passing: 'Graduating', failingPrefix: 'Not Graduating' },
  degreeClassVisibility: 'auto',
  checkFinalYear: true,
  checkOutstandingCourses: true,
  checkMinUnits: true,
  columns: [] as ColumnDraft[],
  reportTitle: 'SUMMARY FOR PRESENTATION OF EXAMINATION RESULTS',
  headerSchoolName: '',
  headerAffiliateText: '',
  headerLogoPosition: 'left',
  headerSemesterLabel: 'SEMESTER:',
  headerSemesterFormat: '',
  headerLevelLabel: 'LEVEL:',
  headerLevelDisplayMap: [] as { level: string; display: string }[],
  paperSize: 'A4',
  orientation: 'landscape',
  rowsPerPage: 23,
  maxColumnsPerPage: 20,
  showSignatures: true,
  signatures: [] as SignatureEntry[],
  sectionTitles: {
    classSummary: 'Class Performance Summary',
    interpretation: 'INTERPRETATION OF TERMS',
    failedCourses: 'LIST OF FAILED COMPULSORY COURSES',
    courseKey: 'COURSE KEY',
  },
  legendGroups: [] as LegendGroup[],
  legendColumns: 2,
});

const DEFAULT_CGPA_COLUMNS: Omit<ColumnDraft, 'tempId' | 'id'>[] = [
  { label: 'SN',           key: 'sn',            type: 'STUDENT_INFO', group: null,         is_visible: true, calculation_function: null,         scope: null },
  { label: 'Matric No',    key: 'matric_no',      type: 'STUDENT_INFO', group: null,         is_visible: true, calculation_function: null,         scope: null },
  { label: 'Names',        key: 'names',          type: 'STUDENT_INFO', group: null,         is_visible: true, calculation_function: null,         scope: null },
  { label: 'TCP',          key: 'prev_tcp',       type: 'CALCULATED',   group: 'Previous',   is_visible: true, calculation_function: 'TCP',         scope: 'previous' },
  { label: 'TNUP',         key: 'prev_tnup',      type: 'CALCULATED',   group: 'Previous',   is_visible: true, calculation_function: 'TNUP',        scope: 'previous' },
  { label: 'TNUR',         key: 'prev_tnur',      type: 'CALCULATED',   group: 'Previous',   is_visible: true, calculation_function: 'TNUR',        scope: 'previous' },
  { label: 'CGPA',         key: 'prev_cgpa',      type: 'CALCULATED',   group: 'Previous',   is_visible: true, calculation_function: 'CGPA',        scope: 'previous' },
  { label: 'TCP',          key: 'curr_tcp',       type: 'CALCULATED',   group: 'Current',    is_visible: true, calculation_function: 'TCP',         scope: 'current' },
  { label: 'TNUP',         key: 'curr_tnup',      type: 'CALCULATED',   group: 'Current',    is_visible: true, calculation_function: 'TNUP',        scope: 'current' },
  { label: 'TNUR',         key: 'curr_tnur',      type: 'CALCULATED',   group: 'Current',    is_visible: true, calculation_function: 'TNUR',        scope: 'current' },
  { label: 'GPA',          key: 'curr_gpa',       type: 'CALCULATED',   group: 'Current',    is_visible: true, calculation_function: 'GPA',         scope: 'current' },
  { label: 'TCP',          key: 'cum_tcp',        type: 'CALCULATED',   group: 'Cumulative', is_visible: true, calculation_function: 'TCP',         scope: 'cumulative' },
  { label: 'TNUP',         key: 'cum_tnup',       type: 'CALCULATED',   group: 'Cumulative', is_visible: true, calculation_function: 'TNUP',        scope: 'cumulative' },
  { label: 'TNUR',         key: 'cum_tnur',       type: 'CALCULATED',   group: 'Cumulative', is_visible: true, calculation_function: 'TNUR',        scope: 'cumulative' },
  { label: 'CGPA',         key: 'cum_cgpa',       type: 'CALCULATED',   group: 'Cumulative', is_visible: true, calculation_function: 'CGPA',        scope: 'cumulative' },
  { label: 'Failed Courses',key: 'failed_courses',type: 'STATUS',       group: null,         is_visible: true, calculation_function: 'FAILED_COURSES', scope: null },
  { label: 'Degree Class', key: 'degree_class',   type: 'STATUS',       group: null,         is_visible: true, calculation_function: 'DEGREE_CLASS',   scope: null },
  { label: 'Status',       key: 'status',         type: 'STATUS',       group: null,         is_visible: true, calculation_function: 'STATUS',         scope: null },
];

const initForm = () => {
  if (props.initialData) {
    form.value.name = props.initialData.name + (props.isClone ? ' (Copy)' : '');
    form.value.description = props.initialData.description ?? '';
    form.value.id = props.isClone ? null : props.initialData.id;
    const p = (props.initialData.properties ?? {}) as Record<string, unknown>;
    form.value.calculationMethod = (p.calculation_method as string) ?? 'cgpa';
    form.value.showGradeSummary = (p.show_grade_summary as boolean) ?? true;
    form.value.showFailedCourses = (p.show_failed_courses as boolean) ?? true;
    form.value.showLegend = (p.show_legend as boolean) ?? true;

    const rawGrad = (p.graduating_categories as CategoryEntry[]) ?? defaultGraduatingCategories(form.value.calculationMethod);
    form.value.graduatingCategories = rawGrad.map(c => ({ ...c }));
    const rawNonGrad = (p.non_graduating_statuses as CategoryEntry[]) ?? defaultNonGraduatingStatuses(form.value.calculationMethod);
    form.value.nonGraduatingStatuses = rawNonGrad.map(c => ({ ...c }));
    const statusLabels = (p.graduating_status_labels ?? {}) as Record<string, string>;
    form.value.graduatingStatusLabels = {
      passing: statusLabels.passing ?? 'Graduating',
      failingPrefix: statusLabels.failing_prefix ?? 'Not Graduating',
    };
    form.value.degreeClassVisibility = (p.degree_class_visibility as string) ?? 'auto';

    const gradConds = (p.graduation_conditions ?? {}) as Record<string, boolean>;
    form.value.checkFinalYear = gradConds.check_final_year ?? true;
    form.value.checkOutstandingCourses = gradConds.check_outstanding_courses ?? true;
    form.value.checkMinUnits = gradConds.check_min_units ?? true;

    form.value.columns = (props.initialData.columns ?? []).map((c: SummaryTemplateColumn, idx: number) => ({
      tempId: Date.now() + idx,
      id: props.isClone ? null : c.id,
      label: c.label,
      key: c.key,
      type: c.type === 'CALCULATION' ? 'CALCULATED' : c.type,
      group: c.group ?? null,
      is_visible: c.is_visible,
      calculation_function: c.calculation_function ?? null,
      scope: c.calculation_arguments?.scope ?? null,
    }));

    const pdfHeader = (p.pdf_header ?? {}) as Record<string, unknown>;
    form.value.reportTitle = (pdfHeader.report_title as string) ?? 'SUMMARY FOR PRESENTATION OF EXAMINATION RESULTS';
    form.value.headerSchoolName = (pdfHeader.school_name as string) ?? '';
    form.value.headerAffiliateText = (pdfHeader.affiliate_text as string) ?? '';
    form.value.headerLogoPosition = (pdfHeader.logo_position as string) ?? 'left';
    form.value.headerSemesterLabel = (pdfHeader.semester_label as string) ?? 'SEMESTER:';
    form.value.headerSemesterFormat = (pdfHeader.semester_format as string) ?? '';
    form.value.headerLevelLabel = (pdfHeader.level_label as string) ?? 'LEVEL:';
    const rawLevelMap = (pdfHeader.level_display_map ?? {}) as Record<string, string>;
    form.value.headerLevelDisplayMap = Object.entries(rawLevelMap).map(([level, display]) => ({ level, display }));
    form.value.paperSize = (p.paper_size as string) ?? 'A4';
    form.value.orientation = (p.orientation as string) ?? 'landscape';
    form.value.rowsPerPage = (p.rows_per_page as number) ?? 23;
    form.value.maxColumnsPerPage = (p.max_columns_per_page as number) ?? 20;
    form.value.showSignatures = (p.show_signatures as boolean) ?? true;

    const rawSigs = (p.signatures as SignatureEntry[]) ?? null;
    form.value.signatures = rawSigs
      ? rawSigs.map(s => ({ ...s }))
      : [
        { title: 'Head of Department', show_sign: true, show_date: true },
        { title: 'Dean', show_sign: true, show_date: true },
        { title: 'External Examiner', show_sign: true, show_date: true },
      ];

    const sectionTitles = (p.section_titles ?? {}) as Record<string, string>;
    form.value.sectionTitles = {
      classSummary: sectionTitles.class_summary ?? 'Class Performance Summary',
      interpretation: sectionTitles.interpretation ?? 'INTERPRETATION OF TERMS',
      failedCourses: sectionTitles.failed_courses ?? 'LIST OF FAILED COMPULSORY COURSES',
      courseKey: sectionTitles.course_key ?? 'COURSE KEY',
    };

    const legendProps = (p.legend ?? {}) as Record<string, unknown>;
    form.value.legendColumns = (legendProps.columns as number) ?? 2;
    const rawGroups = legendProps.groups as LegendGroup[] | undefined;
    if (rawGroups && rawGroups.length > 0) {
      form.value.legendGroups = rawGroups.map(g => ({ heading: g.heading ?? '', lines: [...(g.lines ?? [])] }));
    } else {
      const rawLines = legendProps.interpretation_lines as string[] | undefined;
      if (rawLines && rawLines.length > 0) {
        form.value.legendGroups = [{ heading: '', lines: [...rawLines] }];
      } else {
        form.value.legendGroups = [];
      }
    }

    showClassification.value = true;
  } else {
    form.value.name = '';
    form.value.description = '';
    form.value.id = null;
    form.value.calculationMethod = 'cgpa';
    form.value.showGradeSummary = true;
    form.value.showFailedCourses = true;
    form.value.showLegend = true;
    form.value.graduatingCategories = defaultGraduatingCategories('cgpa');
    form.value.nonGraduatingStatuses = defaultNonGraduatingStatuses('cgpa');
    form.value.graduatingStatusLabels = { passing: 'Graduating', failingPrefix: 'Not Graduating' };
    form.value.degreeClassVisibility = 'auto';
    form.value.checkFinalYear = true;
    form.value.checkOutstandingCourses = true;
    form.value.checkMinUnits = true;
    form.value.columns = DEFAULT_CGPA_COLUMNS.map((c, idx) => ({
      ...c,
      tempId: Date.now() + idx,
      id: null,
    }));
    form.value.reportTitle = 'SUMMARY FOR PRESENTATION OF EXAMINATION RESULTS';
    form.value.headerSchoolName = '';
    form.value.headerAffiliateText = '';
    form.value.headerLogoPosition = 'left';
    form.value.headerSemesterLabel = 'SEMESTER:';
    form.value.headerSemesterFormat = '';
    form.value.headerLevelLabel = 'LEVEL:';
    form.value.headerLevelDisplayMap = [];
    form.value.paperSize = 'A4';
    form.value.orientation = 'landscape';
    form.value.rowsPerPage = 23;
    form.value.maxColumnsPerPage = 20;
    form.value.showSignatures = true;
    form.value.signatures = [
      { title: 'Head of Department', show_sign: true, show_date: true },
      { title: 'Dean', show_sign: true, show_date: true },
      { title: 'External Examiner', show_sign: true, show_date: true },
    ];
    form.value.sectionTitles = {
      classSummary: 'Class Performance Summary',
      interpretation: 'INTERPRETATION OF TERMS',
      failedCourses: 'LIST OF FAILED COMPULSORY COURSES',
      courseKey: 'COURSE KEY',
    };
    form.value.legendGroups = [];
    form.value.legendColumns = 2;
  }
};

watch(() => props.initialData, initForm, { immediate: true });

const addColumnMenuRef = ref();
const toggleAddColumnMenu = (event: Event) => {
  addColumnMenuRef.value.toggle(event);
};

const addPresetColumn = (preset: { label: string; key: string; type: string; group: string | null; calculation_function: string | null; scope: string | null }) => {
  const existingKeys = new Set(form.value.columns.map(c => c.key));
  let key = preset.key;
  if (existingKeys.has(key)) {
    let i = 2;
    while (existingKeys.has(`${key}_${i}`)) i++;
    key = `${key}_${i}`;
  }
  form.value.columns.push({
    tempId: Date.now(),
    id: null,
    label: preset.label,
    key,
    type: preset.type,
    group: preset.group,
    is_visible: true,
    calculation_function: preset.calculation_function,
    scope: preset.scope,
  });
};

const addColumnMenuItems = [
  {
    label: 'Student Info',
    items: [
      { label: 'S/N', command: () => addPresetColumn({ label: 'SN', key: 'sn', type: 'STUDENT_INFO', group: null, calculation_function: null, scope: null }) },
      { label: 'Matric No', command: () => addPresetColumn({ label: 'Matric No', key: 'matric_no', type: 'STUDENT_INFO', group: null, calculation_function: null, scope: null }) },
      { label: 'Name', command: () => addPresetColumn({ label: 'Names', key: 'names', type: 'STUDENT_INFO', group: null, calculation_function: null, scope: null }) },
      { label: 'Campus', command: () => addPresetColumn({ label: 'Campus', key: 'campus', type: 'STUDENT_INFO', group: null, calculation_function: null, scope: null }) },
    ],
  },
  {
    label: 'Calculated',
    items: [
      { label: 'TCP', command: () => addPresetColumn({ label: 'TCP', key: 'tcp', type: 'CALCULATED', group: null, calculation_function: 'TCP', scope: 'cumulative' }) },
      { label: 'TNUP', command: () => addPresetColumn({ label: 'TNUP', key: 'tnup', type: 'CALCULATED', group: null, calculation_function: 'TNUP', scope: 'cumulative' }) },
      { label: 'TNUR', command: () => addPresetColumn({ label: 'TNUR', key: 'tnur', type: 'CALCULATED', group: null, calculation_function: 'TNUR', scope: 'cumulative' }) },
      { label: 'GPA', command: () => addPresetColumn({ label: 'GPA', key: 'gpa', type: 'CALCULATED', group: null, calculation_function: 'GPA', scope: 'cumulative' }) },
      { label: 'CGPA', command: () => addPresetColumn({ label: 'CGPA', key: 'cgpa', type: 'CALCULATED', group: null, calculation_function: 'CGPA', scope: 'cumulative' }) },
    ],
  },
  {
    label: 'Status & Classification',
    items: [
      { label: 'Status', command: () => addPresetColumn({ label: 'Status', key: 'status', type: 'STATUS', group: null, calculation_function: 'STATUS', scope: null }) },
      { label: 'Degree Class', command: () => addPresetColumn({ label: 'Degree Class', key: 'degree_class', type: 'STATUS', group: null, calculation_function: 'DEGREE_CLASS', scope: null }) },
      { label: 'Failed Courses', command: () => addPresetColumn({ label: 'Failed Courses', key: 'failed_courses', type: 'STATUS', group: null, calculation_function: 'FAILED_COURSES', scope: null }) },
      { label: 'Outstanding Count', command: () => addPresetColumn({ label: 'Outstd', key: 'outstd_count', type: 'STATUS', group: null, calculation_function: 'OUTSTANDING_COUNT', scope: null }) },
    ],
  },
  { separator: true },
  {
    label: 'Custom (Blank)',
    command: () => addPresetColumn({ label: 'New Column', key: 'new_col', type: 'CUSTOM', group: null, calculation_function: null, scope: null }),
  },
];

const removeColumn = (index: number) => {
  form.value.columns.splice(index, 1);
};

const updateCalcArgs = (item: ColumnDraft) => {
  if (item.scope) {
    item.calculation_arguments = { scope: item.scope };
  }
};

const onTypeChange = (item: ColumnDraft) => {
  if (item.type === 'STUDENT_INFO' || item.type === 'CUSTOM') {
    item.calculation_function = null;
    item.scope = null;
    item.calculation_arguments = null;
    item.group = null;
  } else if (item.type === 'STATUS') {
    item.calculation_function = null;
    item.scope = null;
    item.calculation_arguments = null;
  } else if (item.type === 'CALCULATED') {
    item.calculation_function = null;
    item.scope = null;
    item.calculation_arguments = null;
  }
};

// Drag-and-drop
const columnsRef = computed(() => form.value.columns);
const { draggedIndex, dropTargetIndex, onDragStart, onDragEnter, onDragEnd, onDrop } = useDragDropList(columnsRef);

const { mutate: createTemplate, loading: creating } = useCreateSummaryTemplateMutation();
const { mutate: updateTemplate, loading: updating } = useUpdateSummaryTemplateMutation();
const saving = computed(() => creating.value || updating.value);

const save = async() => {
  if (!form.value.name) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Name is required', life: 3000 });
    return;
  }

  const keys = form.value.columns.map(c => c.key);
  const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
  if (dupes.length > 0) {
    toast.add({ severity: 'error', summary: 'Duplicate Keys', detail: `Duplicate column keys: ${[...new Set(dupes)].join(', ')}`, life: 5000 });
    return;
  }

  const columnsInput = form.value.columns.map((c, index) => ({
    id: c.id,
    label: c.label,
    key: c.key,
    type: c.type,
    group: c.group,
    is_visible: c.is_visible,
    sequence: index + 1,
    calculation_function: c.calculation_function ?? null,
    calculation_arguments: c.scope ? { scope: c.scope } : null,
  }));

  const properties: Record<string, unknown> = {
    calculation_method: form.value.calculationMethod,
    show_grade_summary: form.value.showGradeSummary,
    show_failed_courses: form.value.showFailedCourses,
    show_legend: form.value.showLegend,
    graduating_categories: form.value.graduatingCategories,
    non_graduating_statuses: form.value.nonGraduatingStatuses,
    graduating_status_labels: {
      passing: form.value.graduatingStatusLabels.passing || 'Graduating',
      failing_prefix: form.value.graduatingStatusLabels.failingPrefix || 'Not Graduating',
    },
    degree_class_visibility: form.value.degreeClassVisibility,
    graduation_conditions: {
      check_final_year: form.value.checkFinalYear,
      check_outstanding_courses: form.value.checkOutstandingCourses,
      check_min_units: form.value.checkMinUnits,
    },
    pdf_header: {
      report_title: form.value.reportTitle || 'SUMMARY FOR PRESENTATION OF EXAMINATION RESULTS',
      ...(form.value.headerSchoolName ? { school_name: form.value.headerSchoolName } : {}),
      ...(form.value.headerAffiliateText ? { affiliate_text: form.value.headerAffiliateText } : {}),
      logo_position: form.value.headerLogoPosition,
      semester_label: form.value.headerSemesterLabel || 'SEMESTER:',
      ...(form.value.headerSemesterFormat ? { semester_format: form.value.headerSemesterFormat } : {}),
      level_label: form.value.headerLevelLabel || 'LEVEL:',
      ...(form.value.headerLevelDisplayMap.length > 0 ? {
        level_display_map: Object.fromEntries(
          form.value.headerLevelDisplayMap
            .filter(e => e.level && e.display)
            .map(e => [e.level, e.display]),
        ),
      } : {}),
    },
    paper_size: form.value.paperSize,
    orientation: form.value.orientation,
    rows_per_page: form.value.rowsPerPage,
    max_columns_per_page: form.value.maxColumnsPerPage,
    show_signatures: form.value.showSignatures,
    signatures: form.value.signatures,
    section_titles: {
      class_summary: form.value.sectionTitles.classSummary,
      interpretation: form.value.sectionTitles.interpretation,
      failed_courses: form.value.sectionTitles.failedCourses,
      course_key: form.value.sectionTitles.courseKey,
    },
    legend: {
      columns: form.value.legendColumns,
      groups: form.value.legendGroups
        .map(g => ({ heading: g.heading, lines: g.lines.filter(l => l.trim()) }))
        .filter(g => g.lines.length > 0),
    },
  };

  // Preserve any other properties from the original template (e.g. pdf_header, programme_preset)
  if (props.initialData?.properties) {
    const existing = props.initialData.properties as Record<string, unknown>;
    for (const key of Object.keys(existing)) {
      if (!(key in properties)) {
        properties[key] = existing[key];
      }
    }
  }

  try {
    if (isEditing.value && form.value.id) {
      await updateTemplate({ input: { id: form.value.id, name: form.value.name, description: form.value.description, properties, columns: columnsInput } });
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Template updated', life: 3000 });
    } else {
      await createTemplate({ input: { name: form.value.name, description: form.value.description, properties, columns: columnsInput } });
      toast.add({ severity: 'success', summary: 'Created', detail: 'Template created', life: 3000 });
    }
    emit('saved');
  } catch (e) {
    const error = e as Error;
    toast.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
  }
};
</script>
